<template>
  <ValidationField
    v-slot="{ field, errors }"
    v-model="inputValue"
    :name="name"
    as="div"
    :rules="rules"
    :vid="vid"
  >
    <div
      :class="{
        'date-field': true,
        'w-full': fullWidth,
        ...className
      }"
    >
      <span class="date-field__label">
        {{ label }}
      </span>
      <Datepicker
        v-bind="field"
        v-model="inputValue"
        :class="{'date-field--error': errors.length > 0}"
        :disabled="disabled"
        :range="range"
        :min-date="minDate"
        :max-date="maxDate"
        :placeholder="placeholder"
        :format="customFormatter"
        :clearable="true"
        :enable-time-picker="false"
        :month-change-on-scroll="false"
        week-start="0"
        locale="ja"
        year-first
        auto-apply
        no-today
        no-disabled-range
        @update:model-value="dateChange"
      >
        <template #year="{ value }">
          {{ value }}年
        </template>
      </Datepicker>
      <div
        v-if="errors.length > 0"
        style="height: 26px;"
        class="con-text-validation span-text-validation-danger vs-input--text-validation-span v-enter-to"
      >
        <span class="span-text-validation">
          {{ errors[0] }}
        </span>
      </div>
    </div>
  </ValidationField>
</template>

<script>

import Datepicker from '@vuepic/vue-datepicker'
import moment from 'moment'

export default {
  name: 'DateField',
  components: {
    Datepicker
  },
  props: {
    modelValue: {
      type: [String, Array],
      default: null
    },
    name: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    rules: {
      type: String,
      default: null
    },
    vid: {
      type: String,
      default: null
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    onChange: {
      type: Function,
      default: () => {}
    },
    range: {
      type: Boolean,
      default: false
    },
    minDate: {
      type: Date,
      default: null
    },
    maxDate: {
      type: Date,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    format: {
      type: String,
      default: 'YYYY-MM-DD'
    }
  },
  emits: ['update:modelValue'],
  computed: {
    inputValue: {
      get () {
        if (this.range) {
          if (Array.isArray(this.modelValue)) {
            return [moment(this.modelValue[0]).format(this.format), moment(this.modelValue[1]).format(this.format)]
          } else {
            return null
          }
        }
        return this.modelValue ? moment(this.modelValue).format(this.format) : null
      },
      set (newValue) {
        if (newValue === null) {
          return this.$emit('update:modelValue', null)
        } else if (this.range && Array.isArray(newValue)) {
          return this.$emit('update:modelValue', [moment(newValue[0]).format(this.format), moment(newValue[1]).format(this.format)])
        }
        return this.$emit('update:modelValue', moment(newValue).format(this.format))
      }
    },
    className () {
      return {
        'w-full': this.fullWidth
      }
    }
  },
  methods: {
    customFormatter (date) {
      if (Array.isArray(date)) {
        const start = moment(date[0]).format(this.format)
        const end = moment(date[1]).format(this.format)
        return `${start} 〜 ${end}`
      }
      return moment(date).format(this.format)
    },
    dateChange (date) {
      if (this.onChange) {
        if (date === null) {
          this.onChange(null)
        } else if (this.range) {
          const start = moment(date[0]).format(this.format)
          const end = moment(date[1]).format(this.format)
          this.onChange([start, end])
        } else {
          this.onChange(moment(date).format(this.format))
        }
      }
    }
  }
}
</script>

<style scoped>
.date-field {
  width: 200px;
}
.date-field .date-field__label {
  letter-spacing: 0.01rem;
  direction: ltr;
  --vs-light: 245,245,245;
  --vs-primary: 115,103,240;
  --vs-success: 40,199,111;
  --vs-danger: 234,84,85;
  --vs-warning: 255,159,67;
  --vs-dark: 30,30,30;
  --vh: 8.14px;
  font-family: "Montserrat", Helvetica, Arial, sans-serif;
  font-weight: 400;
  line-height: 2 !important;
  text-align: left !important;
  margin: 0;
  box-sizing: border-box;
  outline: none;
  text-transform: none;
  text-decoration: none;
  font-size: .85rem;
  color: rgba(0,0,0,.7);
  padding: 0 0 0 5px;
}
.date-field :deep(.dp__input) {
  line-height: normal;
  padding: 0.7rem 1rem 0.7rem 3rem !important;
  padding-inline-start:var(--dp-input-icon-padding);
}
.date-field--error :deep(.dp__input) {
  border: 1px solid rgba(var(--vs-danger),1)!important;
}
</style>
